import { useQuery } from "@apollo/client/react";
import {
  cloneElement,
  isValidElement,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useContext,
  useMemo,
} from "react";

import { setDialog } from "@/components/app/dialogs/dialog-provider";
import type { ModalBuySubscriptionProps } from "@/components/app/modal-buy-subscription";
import type { ButtonProps } from "@/components/ui/button";
import { Button } from "@/components/ui/button";
import { UserContext } from "@/contexts/user-context";
import {
  SubscriptionByCreatorAndSubscriberDocument,
  SubscriptionTiersByCreatorPublicKeyDocument,
} from "@/graphql/codegen/graphql";
import { DIALOG } from "@/utils/dialog-routes";
import {
  parseTier,
  SubscriptionStatus,
  type TierSchema,
} from "@/utils/subscriptions";

type SubscribeButtonProps = ButtonProps &
  Omit<
    ModalBuySubscriptionProps,
    "open" | "setOpen" | "subscriberPublicKey" | "onSubscribe"
  > & {
    dialogReplace?: boolean;
    messageId?: string;
    postHash?: string;
    render?: (renderProps: {
      isSubscribed: boolean;
      activeTierMemberships: TierSchema[];
    }) => ReactNode;
    renderContent?: (renderProps: {
      isSubscribed: boolean;
      activeTierMemberships: TierSchema[];
    }) => ReactNode;
    onSubscribeSuccess?: () => void;
    loadTierMembershipsOnRender?: boolean;
    resolveVariant?: (renderProps: {
      isSubscribed: boolean;
      activeTierMemberships: TierSchema[];
    }) => ButtonProps["variant"];
    renderTrigger?: (renderProps: {
      isSubscribed: boolean;
      activeTierMemberships: TierSchema[];
      loading: boolean;
      openDialog: () => void;
    }) => ReactNode;
  };

export const SubscribeButton = ({
  creator,
  dialogReplace = false,
  renderContext,
  subscriptionAccessGroupKeyName,
  oneTimePriceToUnlockUsdCents,
  onUnsubscribe,
  messageId,
  postHash,
  render,
  renderContent: RenderContent,
  onSubscribeSuccess,
  loadTierMembershipsOnRender = false,
  resolveVariant,
  renderTrigger,
  variant,
  size = "sm",
  className,
  loading: loadingProp = false,
  children,
  ...buttonProps
}: SubscribeButtonProps) => {
  const { currentUserPublicKey } = useContext(UserContext);
  const {
    data: isSubscribedData,
    loading: loadingIsSubscribedData,
    refetch: refetchIsSubscribedData,
  } = useQuery(SubscriptionByCreatorAndSubscriberDocument, {
    variables: {
      viewerPublicKey: currentUserPublicKey,
      subscriberPublicKey: currentUserPublicKey,
      creatorPublicKey: creator.publicKey || "",
    },
    fetchPolicy: "cache-and-network",
    skip: !currentUserPublicKey || !creator.publicKey,
  });
  const {
    data: allCreatorTiersData,
    loading: loadingAllCreatorTiersData,
    refetch: refetchAllCreatorTiersData,
  } = useQuery(SubscriptionTiersByCreatorPublicKeyDocument, {
    variables: {
      filter: {
        accessGroupOwnerPublicKey: { equalTo: creator.publicKey || "" },
        paymentCadenceDays: { notEqualTo: -1 },
      },
      viewerPublicKey: currentUserPublicKey,
    },
    fetchPolicy: "cache-and-network",
    skip:
      !loadTierMembershipsOnRender ||
      !currentUserPublicKey ||
      !creator.publicKey,
  });

  const isSubscribed = useMemo(
    () =>
      (isSubscribedData?.focusSubscriptions?.nodes?.length ?? 0) > 0 &&
      !!isSubscribedData?.focusSubscriptions?.nodes?.find(
        (s) => s?.extraData?.Status === SubscriptionStatus.Active,
      ),
    [isSubscribedData],
  );

  const activeTierMemberships = useMemo(
    () =>
      allCreatorTiersData?.subscriptionTiers?.nodes?.reduce<TierSchema[]>(
        (acc, tier) => {
          if (
            !tier ||
            (tier.accessGroup?.viewerAccessGroupMemberEntry?.totalCount ??
              0) === 0
          ) {
            return acc;
          }

          acc.push(parseTier(tier));
          return acc;
        },
        [],
      ) ?? [],
    [allCreatorTiersData],
  );

  const renderProps = {
    isSubscribed,
    activeTierMemberships,
  };

  const loading =
    loadingProp ||
    (loadingIsSubscribedData && !isSubscribedData) ||
    (loadTierMembershipsOnRender &&
      loadingAllCreatorTiersData &&
      !allCreatorTiersData);
  const openDialog = () => {
    if (!currentUserPublicKey || !creator.publicKey) {
      return;
    }

    setDialog(DIALOG.BuySubscription, {
      open: true,
      replace: dialogReplace,
      params: {
        creatorPublicKey: creator.publicKey,
        renderContext,
        ...(creator.username ? { creatorUsername: creator.username } : {}),
        ...(messageId ? { messageId } : {}),
        ...(typeof oneTimePriceToUnlockUsdCents === "number"
          ? {
              oneTimePriceToUnlockUsdCents: String(
                oneTimePriceToUnlockUsdCents,
              ),
            }
          : {}),
        ...(postHash ? { postHash } : {}),
        ...(subscriptionAccessGroupKeyName
          ? { subscriptionAccessGroupKeyName }
          : {}),
      },
      props: {
        onSubscribeSuccess: async () => {
          await Promise.all([
            refetchIsSubscribedData(),
            loadTierMembershipsOnRender
              ? refetchAllCreatorTiersData()
              : Promise.resolve(),
          ]);

          await onSubscribeSuccess?.();
        },
        onUnsubscribe: async () => {
          await Promise.all([
            refetchIsSubscribedData(),
            loadTierMembershipsOnRender
              ? refetchAllCreatorTiersData()
              : Promise.resolve(),
          ]);

          await onUnsubscribe?.();
        },
      },
    });
  };

  const trigger = renderTrigger?.({
    ...renderProps,
    loading,
    openDialog,
  }) ?? (
    <Button
      {...buttonProps}
      variant={
        resolveVariant?.(renderProps) ??
        variant ??
        ((subscriptionAccessGroupKeyName &&
          activeTierMemberships.find(
            (t) => t.accessGroupKeyName === subscriptionAccessGroupKeyName,
          )) ||
        (!subscriptionAccessGroupKeyName && isSubscribed)
          ? "outline"
          : "default")
      }
      size={size}
      className={className}
      loading={loading}
    >
      {RenderContent ? (
        <RenderContent {...renderProps} />
      ) : (
        (render?.(renderProps) ?? children)
      )}
    </Button>
  );

  if (!currentUserPublicKey) {
    return trigger;
  }

  if (!isValidElement(trigger)) {
    return (
      <div className="contents" onClick={openDialog}>
        {trigger}
      </div>
    );
  }

  const triggerProps = trigger.props as {
    disabled?: boolean;
    onClick?: (event: MouseEvent<HTMLElement>) => void;
    onSelect?: (event: Event) => void;
  };

  if (typeof triggerProps.onSelect === "function") {
    return trigger;
  }

  return cloneElement(
    trigger as ReactElement<{
      onClick?: (event: MouseEvent<HTMLElement>) => void;
    }>,
    {
      onClick: (event: MouseEvent<HTMLElement>) => {
        triggerProps.onClick?.(event);

        if (!event.defaultPrevented && !triggerProps.disabled) {
          openDialog();
        }
      },
    },
  );
};
